import { styleConstants } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import { spacing } from 'ext-ui/style/spacing'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import DepartmentsTable from 'src/app/DepartmentsTable/DepartmentsTable'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import Box from 'src/components/Box/Box'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { ExtendLocales } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { useQueryDepartments } from 'src/queries/organizations/department.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { createSetIsAddDeptPopupOpen } from 'src/redux/actions/app.actions'
import { getOrganization } from 'src/redux/actions/data/organizations.actions'
import {
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const Departments: React.FC = () => {
  const user = useSelector(selectUserListModelFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: departmentsList, isFetching: departmentsListLoading } =
    useQueryDepartments({
      organizationId: organizationId,
      includeArchived: false,
    })
  const { data: orgFeatureFlags, isPending: orgFeatureFlagsLoading } =
    useQueryOrganizationFeatureFlags({ orgId: organizationId })
  const { analytics } = useAnalyticsContext()

  const showEmptyState =
    departmentsList === undefined || departmentsList.length === 0

  const dispatch = useBackgroundDispatch()
  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganization(organizationId, true))
    }
    // TODO - should not assume user exists in redux, so should load user here if iti doesn't exist
    // dispatch(getUser)
  }, [organizationId])

  const EmptyStateContent: React.FC = (): React.ReactElement => {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        `}
      >
        <TableEmptyState
          design={
            locale === ExtendLocales.frCA
              ? TableEmptyStateDesigns.DepartmentsFrench
              : TableEmptyStateDesigns.DepartmentsEnglish
          }
          wide
          buttonClickAction={(): void =>
            dispatch(createSetIsAddDeptPopupOpen(true))
          }
        />
      </div>
    )
  }

  const DepartmentsTableContent: React.FC = (): React.ReactElement => {
    return (
      <Box
        noPadding
        noShadow
        outlineBorder
        css={css`
          overflow: hidden;
        `}
      >
        <div
          css={css`
            padding: ${spacing(6)};
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
            background-color: ${styleConstants.colors.white};
            border-bottom: 1px solid ${styleConstants.colors.white};
          `}
        ></div>
        <DepartmentsTable
          departments={departmentsList || []}
          organizationId={organizationId}
        />
      </Box>
    )
  }

  const translate = useTranslate()
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const currentUser = useSelector(selectUserListModelFromState)

  const showLoadingState =
    !loadedRemoteConfig ||
    user === undefined ||
    organization === undefined ||
    departmentsListLoading ||
    orgFeatureFlagsLoading

  if (showLoadingState) {
    return <LoadingBackgroundPage />
  }

  if (
    !currentUser?.hasOrgBookkeeperPermissions ||
    !orgFeatureFlags?.departmentsEnabled
  ) {
    return <NotFoundBackgroundPage />
  }

  return (
    <>
      <Helmet>
        <title>{`Teams | ${appName}`}</title>
        <meta name="description" content={`Teams page of ${appName}`} />
      </Helmet>
      <LayoutPage
        title={<span>{translate('Teams')}</span>}
        actions={
          <Button
            onClick={(): void => {
              analytics.track('departments - add department')
              dispatch(createSetIsAddDeptPopupOpen(true))
            }}
            dropShadow={false}
          >
            {translate('Add Team +')}
          </Button>
        }
      >
        {showEmptyState ? <EmptyStateContent /> : <DepartmentsTableContent />}
      </LayoutPage>
    </>
  )
}

export default Departments
